/**
 * El consumo de tokens y lo que cuesta (2.4).
 *
 * Se ve en cuatro sitios --al pie de cada respuesta, en la cabecera de la
 * conversación, en el contador de arriba y en la vista de Work-- y los cuatro
 * tienen que decirlo IGUAL: mismas palabras para lo estimado y para lo que no
 * tiene precio. Por eso las reglas viven aquí y no en cada componente.
 *
 * Dos reglas que no se negocian:
 *
 * - Todo coste es ESTIMADO. Sale de un catálogo de precios que se sincroniza
 *   desde fuera; la factura del proveedor es la que manda.
 * - Un precio desconocido se DICE. Pintarlo como cero es mentir a la baja, y
 *   un coste parcial sin avisar es lo mismo con más decimales.
 */
import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";

import { request } from "../lib/api-client.ts";
import { API_BASE } from "../lib/api-routes.ts";
import type { Schemas } from "../lib/api-schemas.ts";
import type { operations } from "../lib/api-types.ts";

type UsageSummaryResponse =
  operations["get_usage_summary"]["responses"][200]["content"]["application/json"]["data"];
type ModelPricesResponse =
  operations["list_model_prices"]["responses"][200]["content"]["application/json"]["data"];
type SyncResponse =
  operations["sync_model_prices"]["responses"][201]["content"]["application/json"]["data"];

/** Totales de un conjunto de llamadas: una conversación, un día, un modelo… */
export type UsageTotals = Readonly<Schemas["UsageTotals"]>;
export type UsageSummary = Readonly<UsageSummaryResponse>;
export type ModelPrices = Readonly<ModelPricesResponse>;
export type ModelPrice = ModelPrices["prices"][number];
export type PriceSync = NonNullable<ModelPrices["lastSync"]>;
export type SyncResult = Readonly<SyncResponse>;

/**
 * El aviso de un turno que se pasó del umbral (parte 3, punto 5): leyó más de
 * `threshold` tokens de entrada, y `topTool` es la herramienta cuyos
 * resultados más pesan en lo que lee el modelo.
 */
export type TurnWarning = Readonly<
  NonNullable<Schemas["MessageUsage"]["warning"]>
>;

/**
 * Lo que gastó un turno, en el vocabulario de la interfaz.
 *
 * Es NUESTRO y no el del cable a propósito: llega por dos caminos --el
 * `finish` del stream, que no está en el OpenAPI, y `MessageUsage` al releer
 * la conversación-- y los componentes no tienen por qué saber cuál fue.
 */
export interface TurnUsage {
  readonly input: number;
  readonly output: number;
  readonly cachedInput: number;
  readonly cacheWriteInput: number;
  /** `null`: el modelo no tiene precio conocido. NO es cero. */
  readonly costUsd: number | null;
  /** `null` en mensajes guardados antes de medirlo. */
  readonly model: string | null;
  readonly warning?: TurnWarning;
}

/* ------------------------------------------------------------------------ */
/* Leer lo que llega                                                         */
/* ------------------------------------------------------------------------ */

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const numberOr = (value: unknown, fallback: number): number =>
  typeof value === "number" && Number.isFinite(value) ? value : fallback;

/**
 * El consumo de un turno, venga del `finish` del stream o de `MessageUsage`.
 *
 * El `finish` no está en el OpenAPI --es un evento del stream, no una ruta--,
 * así que se lee con cuidado en la frontera. Y hay un caso que NO es un turno
 * de cero: cuando se resuelve una tarjeta y quedan otras, el turno termina sin
 * llamar al modelo y llega `model: ""` con todo a cero. Ahí no hay nada que
 * contar y se devuelve `undefined`; no es un precio desconocido.
 *
 * `model: null` es otra cosa: un mensaje guardado antes de medirlo. Sí llamó
 * al modelo, solo que no consta cuál.
 */
export const readTurnUsage = (raw: unknown): TurnUsage | undefined => {
  if (!isRecord(raw)) return undefined;

  const input = numberOr(raw["input"], 0);
  const output = numberOr(raw["output"], 0);
  const model = raw["model"];
  if (model === "" || (input === 0 && output === 0)) return undefined;

  const cost = raw["costUsd"];
  const warning = raw["warning"];

  return {
    input,
    output,
    cachedInput: numberOr(raw["cachedInput"], 0),
    cacheWriteInput: numberOr(raw["cacheWriteInput"], 0),
    // Sin coste es desconocido, nunca cero.
    costUsd: typeof cost === "number" && Number.isFinite(cost) ? cost : null,
    model: typeof model === "string" ? model : null,
    ...(isRecord(warning)
      ? {
          warning: {
            threshold: numberOr(warning["threshold"], 0),
            topTool:
              typeof warning["topTool"] === "string"
                ? warning["topTool"]
                : null,
          },
        }
      : {}),
  };
};

/* ------------------------------------------------------------------------ */
/* Sumar                                                                     */
/* ------------------------------------------------------------------------ */

export const EMPTY_TOTALS: UsageTotals = {
  calls: 0,
  input: 0,
  output: 0,
  cachedInput: 0,
  cacheWriteInput: 0,
  costUsd: null,
  unpricedCalls: 0,
};

/**
 * Dos costes juntos.
 *
 * `null + 3` es 3 y no `null`: lo que tiene precio se sigue sumando, y lo que
 * no lo tiene lo apunta `unpricedCalls` aparte. Solo si NINGUNO tiene precio el
 * total es desconocido.
 */
const addCost = (a: number | null, b: number | null): number | null =>
  a === null && b === null ? null : (a ?? 0) + (b ?? 0);

export const addTotals = (a: UsageTotals, b: UsageTotals): UsageTotals => ({
  calls: a.calls + b.calls,
  input: a.input + b.input,
  output: a.output + b.output,
  cachedInput: a.cachedInput + b.cachedInput,
  cacheWriteInput: a.cacheWriteInput + b.cacheWriteInput,
  costUsd: addCost(a.costUsd, b.costUsd),
  unpricedCalls: a.unpricedCalls + b.unpricedCalls,
});

/**
 * Un turno como totales. Cuenta como UNA llamada aunque el bucle haya hecho
 * varias: el turno no dice cuántas, y para el coste da igual.
 */
export const turnTotals = (turn: TurnUsage): UsageTotals => ({
  calls: 1,
  input: turn.input,
  output: turn.output,
  cachedInput: turn.cachedInput,
  cacheWriteInput: turn.cacheWriteInput,
  costUsd: turn.costUsd,
  unpricedCalls: turn.costUsd === null ? 1 : 0,
});

export const sumTurns = (
  turns: readonly (TurnUsage | undefined)[],
): UsageTotals =>
  turns.reduce<UsageTotals>(
    (total, turn) =>
      turn === undefined ? total : addTotals(total, turnTotals(turn)),
    EMPTY_TOTALS,
  );

/** Todos los tokens, de las cuatro clases. */
export const totalTokens = (usage: UsageTotals): number =>
  usage.input + usage.output + usage.cachedInput + usage.cacheWriteInput;

/* ------------------------------------------------------------------------ */
/* Decirlo                                                                   */
/* ------------------------------------------------------------------------ */

const compact = new Intl.NumberFormat("es", {
  notation: "compact",
  maximumFractionDigits: 1,
});

/** `12,3 mil`, `1,2 M`: un contador se mira, no se suma a mano. */
export const formatTokens = (tokens: number): string => compact.format(tokens);

/**
 * Dólares, con los decimales que hagan falta para que no salga cero.
 *
 * Un turno cuesta céntimos o fracciones de céntimo; con dos decimales fijos
 * casi todos dirían `0,00 US$`, que se lee como gratis.
 */
export const formatUsd = (usd: number): string =>
  usd.toLocaleString("es", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: usd !== 0 && Math.abs(usd) < 1 ? 4 : 2,
  });

/** Cómo se lee un coste: nunca como cero si no se sabe. */
export type CostKind = "estimated" | "partial" | "unknown";

export interface CostLabel {
  readonly kind: CostKind;
  /** Solo la cifra, para donde el rótulo ya dice "estimado": `≥ 0,84 US$`. */
  readonly amount: string;
  /** Para un contador: `0,84 US$ est.` */
  readonly short: string;
  /** Para una línea: `0,84 US$ estimado`. */
  readonly medium: string;
  /** Con el porqué, para el tooltip o donde hay sitio. */
  readonly long: string;
}

export const UNKNOWN_PRICE = "precio desconocido";

export const describeCost = (
  costUsd: number | null,
  unpricedCalls = 0,
): CostLabel => {
  if (costUsd === null) {
    return {
      kind: "unknown",
      amount: UNKNOWN_PRICE,
      short: UNKNOWN_PRICE,
      medium: UNKNOWN_PRICE,
      long: UNKNOWN_PRICE,
    };
  }

  const amount = formatUsd(costUsd);
  if (unpricedCalls > 0) {
    const calls =
      unpricedCalls === 1
        ? "1 llamada sin precio conocido"
        : `${unpricedCalls.toLocaleString("es")} llamadas sin precio conocido`;
    // `≥`: lo que falta solo puede sumar.
    return {
      kind: "partial",
      amount: `≥ ${amount}`,
      short: `≥ ${amount} est.`,
      medium: `≥ ${amount} estimado`,
      long: `al menos ${amount} estimado (${calls})`,
    };
  }

  return {
    kind: "estimated",
    amount,
    short: `${amount} est.`,
    medium: `${amount} estimado`,
    long: `${amount} estimado`,
  };
};

/** Cómo se llama cada tarea del libro de consumo. */
const TASK_LABELS: Record<string, string> = {
  chat: "Chat",
  memory_extraction: "Extracción de memoria",
  conversation_compaction: "Compactación",
  conversation_title: "Títulos",
  alert_triage: "Triaje de alertas",
};

/** Una tarea que esta versión no conoce se enseña con su nombre, no se esconde. */
export const taskLabel = (task: string): string => TASK_LABELS[task] ?? task;

/* ------------------------------------------------------------------------ */
/* Pedirlo                                                                   */
/* ------------------------------------------------------------------------ */

/**
 * La clave de todo lo del consumo.
 *
 * Se invalida al terminar cada turno: es cuando cambia. El contador de arriba
 * y la vista de Work cuelgan de ella, así que se refrescan los dos a la vez.
 */
export const USAGE_KEY = ["usage"] as const;

/**
 * Un rango del resumen: los que conoce el backend, o dos días `AAAA-MM-DD`
 * incluidos los dos.
 */
export type UsageRange =
  | { readonly range: "today" | "month" }
  | { readonly from: string; readonly to: string };

const rangeParams = (range: UsageRange): string =>
  new URLSearchParams(
    "range" in range
      ? { range: range.range }
      : { from: range.from, to: range.to },
  ).toString();

export const usageSummaryQuery = (range: UsageRange) =>
  queryOptions({
    queryKey: [...USAGE_KEY, "summary", range] as const,
    queryFn: () =>
      request<UsageSummary>(
        `${API_BASE}/queries/get_usage_summary?${rangeParams(range)}`,
      ),
  });

/**
 * Los precios. Sin `all`, solo los de los modelos que se usan --los
 * configurados y los que ha devuelto el proveedor--; con `all`, todos los de
 * la fuente, que son cientos.
 */
export const modelPricesQuery = (all = false) =>
  queryOptions({
    queryKey: [...USAGE_KEY, "prices", { all }] as const,
    queryFn: () =>
      request<ModelPrices>(
        `${API_BASE}/queries/list_model_prices${all ? "?all=true" : ""}`,
      ),
  });

/**
 * Sincronizar los precios a mano.
 *
 * Que la fuente falle NO es un error HTTP: vuelve `status: "error"` con el
 * mensaje y los precios se quedan como estaban. Por eso quien lo llama mira
 * el resultado y no solo si la petición falló.
 *
 * Al terminar se refresca TODO lo del consumo y no solo la tabla de precios:
 * un modelo que no tenía precio puede tenerlo ahora, aunque los costes ya
 * registrados no cambian --se congelan al registrar la llamada--.
 */
export const useSyncModelPrices = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      request<SyncResult>(`${API_BASE}/commands/sync_model_prices`, {
        method: "POST",
        body: JSON.stringify({}),
      }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: USAGE_KEY }),
  });
};
